import axios, { AxiosResponse } from 'axios'
import {
  AtendimentoResumo,
  Estado,
  AtendimentoDtoUpdate,
  OpenAiPrompt,
  ResponseMessage,
  PromptMessage,
  AtendimentoDtoTokenUpload,
} from './_models'
import { ArquivoDto, AtendimentoDto } from '../../../atendimento/core/_models'
import { pdfBase64ToImages } from '../../../../../../_metronic/helpers'
import { cachedRequest } from '../../../../../../_metronic/helpers/requestCache'

const API_URL = process.env.REACT_APP_API_URL

const ATENDIMENTO_URL = `${API_URL}/atendimentos`

export const getResumoAtendimentos = (query: string): Promise<AtendimentoResumo> => {
  return axios.get(`${ATENDIMENTO_URL}/resumo?${query}`).then((resp) => resp.data)
}

/**
 * Busca resumo de atendimento do profissional autenticado
 * @param query string
 * @returns AtendimentoResumo
 */
export const getResumoAtendimentosProfissional = (query: string): Promise<AtendimentoResumo> => {
  return axios.get(`${ATENDIMENTO_URL}/profissional/resumo?${query}`).then((resp) => resp.data)
}

export const getEstadosUf = (): Promise<Estado[]> => {
  return cachedRequest('estados/uf', () =>
    axios.get(`${API_URL}/estados/uf`).then((resp) => resp.data)
  )
}

export const getEstadoByUf = (uf: string): Promise<Estado> => {
  return axios.get(`${API_URL}/estados/uf/${uf}`).then((resp) => resp.data)
}

/**
 * Diagnóstico pro OpenAI
 * @param atendimentoId Identificador do atendimento
 * @param content prompt/text para enviar
 * @param anexos anexos - ArquivoDto[] - para anexar
 * @returns ResponseMessage
 */
export const getDiagnostic = (
  atendimentoId: number,
  content: string,
  anexos: ArquivoDto[] = []
): Promise<ResponseMessage> => {
  const messages: PromptMessage[] = [
    {
      role: 'system',
      content: [{ type: 'text', text: 'Você é um assistente médico no Brasil.' }],
    },
    {
      role: 'user',
      content: [
        { type: 'text', text: content },
        ...anexos.map((anexo) => ({
          type: 'image_url' as const,
          image_url: {
            url: `data:${anexo.type};base64,${anexo.contentBase64}`,
          },
        })),
      ],
    },
  ]

  const data: OpenAiPrompt = {
    model: 'gpt-4o',
    json: true,
    messages,
  }

  return getOpenAIAnalysis(atendimentoId, data)
}

/**
 * Assume um atendimento de outro profissional
 * @param atendimentoId Identificador do atendimento
 * @returns AtendimentoDto
 */
export const assumeAtendimento = (atendimentoId: number): Promise<AtendimentoDto> => {
  return axios
    .put(`${ATENDIMENTO_URL}/assume/${atendimentoId}`)
    .then((resp: AxiosResponse<AtendimentoDto>) => resp.data)
}

/**
 * Transforma PDF contentBase64 em imagens para processamento
 * @param atendimentoId Identificador do atendimento
 * @param anexo ArquivoDto
 * @returns ResponseMessage
 */
export const readExameIA = async (
  atendimentoId: number,
  anexo: ArquivoDto
): Promise<ResponseMessage> => {
  let imageUrls: string[] = []

  const isPdf = anexo.type?.includes('pdf')

  if (isPdf) {
    if (anexo.contentBase64) {
      imageUrls = await pdfBase64ToImages(anexo.contentBase64)
    } else if (anexo.url) {
      imageUrls = [anexo.url]
    }
  } else {
    if (anexo.url) {
      imageUrls = [anexo.url]
    } else if (anexo.contentBase64) {
      imageUrls = [`data:${anexo.type};base64,${anexo.contentBase64}`]
    }
  }

  const messages: PromptMessage[] = [
    {
      role: 'system',
      content: [
        {
          type: 'text',
          text: `Você é um assistente médico especializado em leitura de exames laboratoriais.
Responda **somente com HTML puro**, sem markdown, sem comentários e sem explicações.`,
        },
      ],
    },
    {
      role: 'user',
      content: [
        {
          type: 'text',
          text: `Leia o documento (pode conter texto OCR ou extraído de PDF) e siga as regras abaixo:
          
1️⃣ Se o texto contiver **exames laboratoriais com resultados numéricos**, extraia-os e formate assim:
<ol>
  <li><strong>Nome do Exame</strong>: Resultado + unidade</li>
  ...
</ol>

2️⃣ Se **não houver exames detectáveis** (ex: resultados numéricos, unidades, tabelas laboratoriais), apenas **transcreva o texto do documento** com o máximo de fidelidade, mantendo:
- Parágrafos
- Quebras de linha
- Espaçamento entre seções

3️⃣ Nunca use blocos de código markdown, apenas HTML.

Responda apenas com o HTML completo.`,
        },
        ...imageUrls.map((img) => ({
          type: 'image_url' as const,
          image_url: { url: img },
        })),
      ],
    },
  ]

  const data: OpenAiPrompt = {
    model: 'gpt-4o',
    json: false,
    messages,
  }

  const response = await getOpenAIAnalysis(atendimentoId, data)

  const cleanedContent = response.content?.replace(/```html|```/g, '').trim() ?? ''

  const isEmpty = !cleanedContent || cleanedContent.length < 20

  if (isEmpty) {
    return {
      ...response,
      success: false,
      content: '',
    }
  }

  return { ...response, success: true, content: cleanedContent }
}

/**
 * Altera registro de atendimento
 * @param atendimentoId Identificador do atendimento
 * @returns AtendimentoDtoUpdate
 */
export const updateAtendimento = (
  atendimentoId: number,
  data: AtendimentoDtoUpdate
): Promise<AtendimentoDtoUpdate> => {
  return axios
    .put(`${ATENDIMENTO_URL}/${atendimentoId}`, data)
    .then((resp: AxiosResponse<AtendimentoDtoUpdate>) => resp.data)
}

/**
 * Consulta OpenAI para o atendimento
 * @param atendimentoId Identificador do atendimento
 * @param data OpenAiPrompt
 * @returns ResponseMessage
 */
export const getOpenAIAnalysis = (
  atendimentoId: number,
  data: OpenAiPrompt
): Promise<ResponseMessage> => {
  return axios
    .post(`${ATENDIMENTO_URL}/openai/${atendimentoId}`, data)
    .then((resp: AxiosResponse<ResponseMessage>) => resp.data)
}

/**
 * Gera token para upload de informações do atendimento
 * @param atendimentoId Identificador do atendimento
 * @param data AtendimentoDtoTokenUpload
 * @returns AtendimentoDto
 */
export const generateTokenAnexo = (
  atendimentoId: number,
  data: AtendimentoDtoTokenUpload
): Promise<{ data: string }> => {
  return axios
    .patch(`${ATENDIMENTO_URL}/token/${atendimentoId}`, data)
    .then((resp: AxiosResponse<{ data: string }>) => resp.data)
}
